import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSelector } from '@reduxjs/toolkit';

import type { IUnpickedShifts } from '@/api/types';
import type { ISortedAvailableShift } from '@/types/schedule';
import type { IWeekBlocks } from '@/types/block';

import { useAppDispatch, useAppSelector, useShowAvailableShifts, useTimezone, useWeek } from '@/hooks';
import { SlideUpCart } from '@/components/common';
import {
  getTotalSelectedShiftsDuration,
  getUnpickedShifts,
  groupPickedUpShiftsByDay,
  groupSchedulesInPickupBasket,
} from '@/helpers';
import { PickedShifts, PickedShiftsWithSuccess, PickedShiftsWithWarning } from '@/components/agent/my-schedule/index';
import {
  assignAvailableShifts,
  clearSelectedAvailableShifts,
  toggleIsSelectedAvailableShifts,
} from '@/store/slices/scheduleSlice';
import { selectContractRequirementsAll } from '@/store/selectors/contractSelectors';
import { getContractAdherence } from '@/store/slices/contractSlice';
import { selectSelectedAvailableShifts } from '@/store/selectors/scheduleSelectors';

interface IProps {
  weekBlocks: IWeekBlocks;
}

const shiftPickUpBasketSelector = createSelector(
  selectContractRequirementsAll,
  selectSelectedAvailableShifts,
  (contractRequirementsAll, selectedAvailableShifts) => ({
    contractRequirementsAll,
    selectedAvailableShifts,
  }),
);

const ShiftPickUpBasket = ({ weekBlocks }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { timezoneName } = useTimezone();
  const { selectedWeekId } = useWeek();
  const { isShowAvailableShifts } = useShowAvailableShifts();
  const [groupedShifts, setGroupedShifts] = useState<{ [key: string]: { [key: string]: ISortedAvailableShift[] } }>({});
  const [showSelectedShifts, setShowSelectedShifts] = useState<boolean>(true);
  const [showShiftPickupSuccess, setShowShiftPickupSuccess] = useState<boolean>(false);
  const [showShiftPickupWarning, setShowShiftPickupWarning] = useState<boolean>(false);
  const [unpickedShifts, setUnpickedShifts] = useState<{ [key: string]: ISortedAvailableShift[] }>({});
  const { contractRequirementsAll, selectedAvailableShifts } = useAppSelector(shiftPickUpBasketSelector);

  const handleRemoveShift = (shift: ISortedAvailableShift) => dispatch(toggleIsSelectedAvailableShifts(shift));
  const handleClearSelectedShifts = useCallback(() => dispatch(clearSelectedAvailableShifts()), [dispatch]);

  const handleShowWhetherIsSuccess = (shiftIds: IUnpickedShifts) => {
    if (shiftIds.length === 0) {
      setShowShiftPickupSuccess(true);
      setShowShiftPickupWarning(false);
    } else {
      setShowShiftPickupSuccess(false);
      setShowShiftPickupWarning(true);

      setUnpickedShifts(groupPickedUpShiftsByDay(getUnpickedShifts(shiftIds, selectedAvailableShifts), timezoneName));
    }
    setShowSelectedShifts(false);
  };

  const handleShowSelectedShifts = () => {
    setShowSelectedShifts(true);
    setShowShiftPickupSuccess(false);
    setShowShiftPickupWarning(false);
    handleClearSelectedShifts();
  };

  useEffect(() => {
    setGroupedShifts(groupSchedulesInPickupBasket(selectedAvailableShifts, timezoneName));
  }, [selectedAvailableShifts, timezoneName]);

  useEffect(() => {
    if (isShowAvailableShifts) {
      setShowSelectedShifts(true);
    } else {
      handleClearSelectedShifts();
    }
  }, [isShowAvailableShifts, handleClearSelectedShifts]);

  const handleSubmitShifts = async () => {
    const selectedShiftsIds = selectedAvailableShifts.map(shift => shift.shiftId);
    const { unpickedShifts } = await dispatch(
      assignAvailableShifts({ shiftsIds: selectedShiftsIds, weekBlocks }),
    ).unwrap();
    handleShowWhetherIsSuccess(unpickedShifts);

    const scheduleIds = Array.from(new Set(selectedAvailableShifts.map(shift => shift.scheduleId)));
    const contracts =
      contractRequirementsAll?.filter(contractRequirement =>
        contractRequirement.contractRequirements.some(item =>
          item.contractRequirementScheduleSummary.some(schedule => scheduleIds.includes(schedule.scheduleId)),
        ),
      ) ?? [];

    contracts.forEach(contract =>
      dispatch(getContractAdherence({ contractId: contract.contractId, weekId: selectedWeekId })),
    );
  };

  const basketHeader = (
    <>
      <SlideUpCart.Title>{t`mySchedule.pickUpShiftCart.header`}</SlideUpCart.Title>
      <SlideUpCart.RightText>
        {showSelectedShifts && getTotalSelectedShiftsDuration(selectedAvailableShifts, timezoneName)}
      </SlideUpCart.RightText>
    </>
  );

  return (
    <>
      {isShowAvailableShifts && (
        <SlideUpCart headerChildren={basketHeader}>
          {showSelectedShifts && (
            <PickedShifts
              groupedSelectedShiftsBySchedule={groupedShifts}
              handleClearSelectedShifts={handleClearSelectedShifts}
              handleSubmitShifts={handleSubmitShifts}
              handleRemoveShift={handleRemoveShift}
            />
          )}
          {showShiftPickupSuccess && <PickedShiftsWithSuccess onSubmit={handleShowSelectedShifts} />}
          {showShiftPickupWarning && (
            <PickedShiftsWithWarning onSubmit={handleShowSelectedShifts} unpickedShiftsByDay={unpickedShifts} />
          )}
        </SlideUpCart>
      )}
    </>
  );
};

export { ShiftPickUpBasket };
